// The files inside one fold, as rows. ONE table for all four kinds.
//
// A retainer's signed contract, a supplier contract on a job, the scan of an invoice and
// the clipping of a publication are four different tables on the server and one row
// shape by the time they arrive (clientDocuments/files.js). So they get one column
// config here rather than four, and the only thing that varies per row is which
// endpoint opens it — which is a lookup, not a branch.
//
// READ ONLY. There is no edit, no archive, no delete and no upload in this file. The
// actions are the two every uploaded file in this app has, and they come from
// FileActions unchanged: it already takes `getUrl` as a prop precisely so a new screen
// can point it at a different signed-URL source, and the open/alert behaviour inside it
// is subtle enough that a second copy would be a liability (see FileList.js).
import { useLookups } from '../../context/LookupsContext';
import he from '../../i18n/he';
import { documentUrls } from '../../api';
import DataTable from '../../components/DataTable';
import DateText, { formatDate } from '../../components/DateText';
import { FileActions } from '../../components/FileList';

// What the file hangs off, when that is not already the fold you are looking at.
//
// An agreement gets none on purpose: it belongs to the retainer or the job whose fold it
// is inside, and a column repeating the header above it is noise — the same reason
// AgreementList carries no owner column. An invoice scan and a clipping DO need one,
// because a retainer has eleven invoices and a job several publications, and "which of
// them is this" is otherwise unanswerable without opening the file.
export const contextOf = (row, lookups) => {
  if (row.kind === 'invoice_file') {
    // A retainer invoice is identified by the month it covers, a job invoice by its
    // description — one or the other, never both (migration 048).
    if (row.period_month) return he.clientDocuments.invoicePeriod(formatDate(row.period_month));
    return row.description || he.clientDocuments.invoiceNoPeriod;
  }
  if (row.kind === 'publication_file') {
    return he.clientDocuments.clipping(row.publication_title, lookups.outletLabel(row.outlet_code));
  }
  return null;
};

export default function DocumentTable({ files, archived, empty }) {
  const lookups = useLookups();

  const columns = [
    // The name leads, with the real filename beneath it — the same pairing AgreementList
    // uses: a partner names a document something they will recognise, and "which scan is
    // this" is answered by the file.
    { key: 'title', label: he.clientDocuments.name, primary: true, render: (r) => (
      <>
        <div>{r.title}</div>
        <span className="doc-file-name" title={r.original_name}>
          {r.original_name}
        </span>
      </>
    ) },
    // Four kinds of document in one list are unreadable without saying which is which.
    { key: 'kind', label: he.clientDocuments.kind, render: (r) => (
      <span className="badge gray">{he.clientDocuments.kinds[r.kind] || r.kind}</span>
    ) },
    { key: 'context', label: he.clientDocuments.belongsTo, render: (r) => {
      const context = contextOf(r, lookups);
      return context ? <span className="muted">{context}</span> : <span className="muted">—</span>;
    } },
    // The date on the DOCUMENT — the contract, the invoice, the day the piece ran — not
    // the upload date. The two are routinely months apart, which is the whole reason
    // every one of the four tables stores its own.
    { key: 'doc_date', label: he.clientDocuments.date, render: (r) => <DateText value={r.doc_date} /> },
    { key: 'partner', label: archived ? he.clientDocuments.archivedBy : he.clientDocuments.uploadedBy,
      render: (r) => (
        // In the archive, who put it away is the more useful fact. A clipping has no
        // archiver — it has no archived state at all — so it falls back to its uploader
        // rather than showing a dash for a question that does not apply to it.
        <span>{(archived ? r.archived_by_partner_name : null) || r.uploaded_by_partner_name || '—'}</span>
      ) },
    { key: 'actions', label: he.common.actions, action: true, render: (r) => (
      // `viewable` was decided by the server from the type detected at upload; the
      // browser never guesses which formats it can render, and one it cannot still
      // downloads. `documentUrls[r.kind]` is the row's own domain's existing endpoint —
      // this screen has none of its own.
      <FileActions file={r} getUrl={documentUrls[r.kind]} />
    ) },
  ];

  return <DataTable columns={columns} rows={files} empty={{ text: empty || he.clientDocuments.emptyNode }} />;
}
